import React from "react";
import { Card, Tooltip } from "flowbite-react";
import { ArrowUp, ArrowDown, Info } from "@phosphor-icons/react";
import { CurrencyCode, useCurrency } from "../contexts/CurrencyContext";
import { HandCoins } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";

interface StatCardProps {
  title: string;
  value: string | number | { value: number | string; label: string; icon: React.ReactNode }[];
  previousValue?: number;
  isCurrency?: boolean;
  isPercentage?: boolean;
  valueInINR?: number;
  icon?: React.ReactNode;
  color?: string;
  onClick?: () => void;
  currency?: CurrencyCode;
  withTaxValue?: number;
  showWithTax?: boolean;
  isInAppPurchase?: boolean;
  totalEstimatedRevenue?: {
    value: number;
    label: string;
    labelClassName?: string;
    valueClassName?: string;
    tooltipItems?: {
      label: string;
      value: number;
      labelClassName?: string;
      valueClassName?: string;
      children?: {
        label: string;
        value: number;
        labelClassName?: string;
        valueClassName?: string;
      }[];
    }[];
  }[]
  subStats?: { value: number | string; label: string; }[]
  tooltip?: boolean;
  tooltipContent?: React.ReactNode;
  tooltipDirection?: "top" | "bottom" | "left" | "right";
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  previousValue,
  isCurrency = false,
  isPercentage = false,
  valueInINR,
  // color = "primary",
  icon,
  onClick,
  currency,
  withTaxValue,
  showWithTax,
  isInAppPurchase,
  totalEstimatedRevenue,
  subStats,
  tooltip = false,
  tooltipContent,
  tooltipDirection = "bottom"
}) => {
  const { formatCurrency } = useCurrency();
  const { t } = useTranslation();

  const percentChange =
    previousValue && typeof value === "number"
      ? ((value - previousValue) / previousValue) * 100
      : 0;

  const isPositive = percentChange >= 0;

  const formatValue = (val: number | string) => {
    if (typeof val === "string") return val;
    if (isCurrency) {
      return formatCurrency(val, currency);
    }
    if (isPercentage) {
      return `${val.toFixed(2)}%`;
    }
    return val.toLocaleString();
  };

  const colorClasses = {
    primary: "bg-primary-50 text-primary-600",
    secondary: "bg-secondary-50 text-secondary-600",
    gray: "bg-gray-100 text-gray-600",
    success: "bg-green-50 text-green-600",
    danger: "bg-red-50 text-red-600",
    warning: "bg-yellow-50 text-yellow-600",
    info: "bg-blue-50 text-blue-600",
    purple: "bg-purple-50 text-purple-600",
    yellow: "bg-yellow-50 text-yellow-600",
    indigo: "bg-[#f5f3ff] text-[#6366f1]",
  };

  // const iconColorClass =
  //   colorClasses[color as keyof typeof colorClasses] || colorClasses.primary;
  const iconColorClass = colorClasses.gray;
  const withTaxEnabled = showWithTax && withTaxValue !== undefined;

  return (
    <Card
      key={title}
      className={`overflow-hidden transition-all duration-200 ${onClick ? "cursor-pointer hover:shadow-md hover:scale-[1.01]" : ""
        }`}
      onClick={onClick}
      theme={{
        root: {
          base: "flex rounded-lg border border-gray-200 bg-white  dark:border-gray-700 dark:bg-gray-800 ",
          children: "flex h-full flex-col justify-center gap-4 p-4 min-h-28",
        },
      }}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500 flex items-center gap-1">
            <span>{title}</span>
            {tooltip ? (
              <Tooltip content={tooltipContent} placement={tooltipDirection}>
                <span className="inline-flex items-center justify-center w-4">
                  <Info size={14} className="text-gray-400 hover:text-gray-600 cursor-help" />
                </span>
              </Tooltip>
            ) : null}
          </p>
          <div className="flex flex-col">
            <span
              className={`transition-all duration-300 leading-none ease-in-out ${withTaxEnabled
                ? "text-lg font-semibold mt-1 text-gray-700"
                : "text-2xl font-bold mt-1 text-gray-700"
                }`}
            >
              {typeof value === "number" || typeof value === "string" ? (
                <>
                  {formatValue(value)}
                </>
              ) : (
                <div className="text-lg flex items-center gap-2">
                  {Array.isArray(value) && value.map((item: any) => (
                    <Tooltip content={item.label} placement="bottom" key={item.label}>
                      <div className="flex items-center gap-2">
                        {item.icon}
                        {formatValue(item.value)}
                      </div>
                    </Tooltip>
                  ))}
                </div>
              )}
            </span>
            {withTaxEnabled && (
              <div className="text-sm text-gray-500 transition-all duration-200 flex gap-2 items-center mt-1">
                {formatValue(withTaxValue)}
                <Tooltip content={t("common.withTax")} placement="bottom">
                  <HandCoins size={20} weight="duotone" />
                </Tooltip>
              </div>
            )}
          </div>
          {valueInINR !== undefined && (
            <p className="text-sm text-gray-500 mt-1">
              ₹
              {valueInINR.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
            </p>
          )}

          {previousValue !== undefined && (
            <div className="flex items-center mt-6">
              <span
                className={`text-sm font-medium ${isPositive ? "text-green-600" : "text-red-600"
                  }`}
              >
                {isPositive ? (
                  <ArrowUp size={16} className="inline" />
                ) : (
                  <ArrowDown size={16} className="inline" />
                )}
                {Math.abs(percentChange).toFixed(1)}%
              </span>
              <span className="text-xs text-gray-500 ml-1">
                {t("common.vsPreviousPeriod")}
              </span>

            </div>
          )}
          {isInAppPurchase && <div className="text-lg flex items-center gap-4 mt-2 text-gray-500">
            {
              totalEstimatedRevenue?.map((item) => (
                <div key={item.label}>
                  {item.tooltipItems && item.tooltipItems.length > 0 ? (
                    <Tooltip
                      placement="bottom"
                      content={
                        <div className="flex flex-col gap-2 min-w-56">
                          {item.tooltipItems.map((tItem) => (
                            <div key={tItem.label} className="flex flex-col gap-1">
                              <div className="flex items-center justify-between gap-6">
                                <span className={`text-xs font-semibold ${tItem.labelClassName || ""}`}>{tItem.label}:</span>
                                <span
                                  className={`text-xs ${tItem.labelClassName?.includes("emerald") || tItem.label.toLowerCase() === "network" ? "text-emerald-400 font-semibold" : ""} ${tItem.valueClassName || ""}`}
                                >
                                  {formatValue(tItem.value)}
                                </span>
                              </div>
                              {tItem.children && tItem.children.length > 0 ? (
                                <div className="pl-3 border-l-2 border-emerald-500 flex flex-col gap-1">
                                  {tItem.children.map((cItem) => (
                                    <div key={cItem.label} className="flex items-center justify-between gap-6">
                                      <span className={`text-xs ${cItem.labelClassName || ""}`}>{cItem.label}</span>
                                      <span className={`text-xs ${cItem.valueClassName || ""}`}>{formatValue(cItem.value)}</span>
                                    </div>
                                  ))}
                                </div>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      }
                    >
                      <h3 className={`text-sm font-bold ${item.labelClassName || ""}`}>
                        {item.label}
                      </h3>
                      <p className={`text-sm ${item.valueClassName || ""}`}>{formatValue(item.value ?? 0)}</p>
                    </Tooltip>
                  ) : (
                    <>
                      <h3 className={`text-sm font-bold ${item.labelClassName || ""}`}>{item.label}</h3>
                      <p className={`text-sm ${item.valueClassName || ""}`}>{formatValue(item.value ?? 0)}</p>
                    </>
                  )}
                </div>
              ))
            }
          </div>}
          {subStats && (
            <div className="flex items-center gap-6 mt-4">
              {subStats.map((item) => (
                <div key={item.label} className="flex flex-col">
                  <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                    {item.label}
                  </span>
                  <span className="text-sm font-semibold text-gray-700">
                    {formatValue(item.value)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {icon && (
          <div className={`p-3 rounded-full ${iconColorClass}`}>{icon}</div>
        )}
      </div>
    </Card>
  )
};

export default StatCard;
